<template>
  <LcForm
    :ref="setFormRef"
    class="w-full flex flex-col gap-200"
    :tracking="{ containerId: 'pick_lottery_numbers_form' }"
  >
    <PickLotteryPlaySlipCard
      class="p-200 gap-200"
      data-containerid="pick_lottery_editor_bet_type"
      :headline="$t('picklottery.bettype.label')"
    >
      <LcFormField v-slot="{ field }" name="addOn">
        <PerPlayAddOnSelector
          v-if="perPlayAddOnValue && perPlayAddOn"
          v-bind="{ ...field, lotteryId }"
          :add-on="perPlayAddOn"
          :label="$t('picklottery.bettype.label')"
          no-teleport
          :value="perPlayAddOnValue"
          @update:value="updatePerPlayAddOn"
        />
      </LcFormField>
      <p v-if="perPlayAddOnValue">
        {{ $t(`lottery.bettype.${perPlayAddOnValue.value}.explanation`) }}
      </p>
    </PickLotteryPlaySlipCard>

    <PickLotteryPlaySlipCard
      class="pt-300 px-200 pb-200 gap-100"
      data-containerid="pick_lottery_editor_numbers"
    >
      <LcFormField v-slot="{ field }" name="numbers">
        <NumberPicker
          v-if="selectedPerPlayAddOn"
          id="number-picker"
          ref="numberPicker"
          v-bind="{ ...field, disabledFields }"
          :fields="config?.numbers"
          :value="numbers"
          @change="updatePlay"
        />
      </LcFormField>
      <LcInfoText v-if="isLockoutLottery" class="text-secondary-secondary" size="small">
        {{ $t('picklottery.disclaimer.lockoutlottery') }}
      </LcInfoText>
    </PickLotteryPlaySlipCard>
  </LcForm>
</template>

<script lang="ts" setup>
import * as Yup from 'yup'

import type {
  AddOnWithValue,
  FieldInput,
  NumbersConfig,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import type { NumberPickerChangeEventValue } from '#product/app/components/NumberPicker.vue'
import type { PlaySlipPlay } from '#product/app/composables/playSlip'

import type { LotterySelectedPerPlayAddOn } from '../../../app/stores/lottery'
import type { PickLotteryStepProps, PickLotteryStepRef } from './types'

type CustomIgnoredForAddOns = Pick<AddOnWithValue, 'name' | 'value'>

type CustomNumberConfig = Pick<NumbersConfig, 'name'> & {
  ignoredForAddOns: CustomIgnoredForAddOns[]
}

type FormValues = Partial<{ addOn: Maybe<string>; numbers: Maybe<number>[] }>

const { play } = defineProps<PickLotteryStepProps>()

const { getTranslation, t } = useI18nUtils()
const {
  config,
  lotteryId,
  perPlayAddOn,
  selectedPerPlayAddOn,
  setSelectedNumbersForField,
  setSelectedPerPlayAddOn,
  setValidPlay,
  validatePlay,
} = usePlaySlip()

const numberPickerRef = useTemplateRef('numberPicker')

const currentAddOn = ref<Maybe<LotterySelectedPerPlayAddOn>>()
const currentFields = ref<FieldInput[]>([])

const currentValues = computed(() => ({
  addOn: play.perPlayAddOn?.value ?? '',
  numbers: numbers.value,
}))
const disabledFields = computed(() => {
  if (!config?.value?.numbers || !play.perPlayAddOn) return []
  return config.value.numbers.reduce(byIgnoredForAddOn(play.perPlayAddOn), [])
})
const isLockoutLottery = computed(() => !!config.value?.isLockoutLottery)
const isPlayComplete = computed(
  () =>
    play.fields.every(
      (field) => field.selected.length > 0 || disabledFields.value.includes(field.name),
    ) && !!play.perPlayAddOn?.value,
)
const numbers = computed(() => play.fields?.flatMap(toSelected) ?? [])
const perPlayAddOnValue = computed(() => play.perPlayAddOn ?? null)

const byIgnoredForAddOn =
  (perPlayAddOn: LotterySelectedPerPlayAddOn) => (acc: string[], number: CustomNumberConfig) =>
    number.ignoredForAddOns.some((addOn) => addOn.value === perPlayAddOn.value)
      ? [...acc, number.name]
      : acc

const focusFirstEnabledInput = () => numberPickerRef.value?.focusFirstEnabledInput()

const invokeValidate = () => validate(currentValues.value)

const toSelected = (field: PlaySlipPlay['fields'][0]) =>
  field?.selected && field.selected.length >= 1 ? field.selected : null

const updatePerPlayAddOn = (perPlayAddOn: LotterySelectedPerPlayAddOn) => {
  setSelectedPerPlayAddOn(play.id, perPlayAddOn)
  focusFirstEnabledInput()
}

const updatePlay = ({ name, value }: NumberPickerChangeEventValue) => {
  const index = play.fields.findIndex((field) => field?.name === name)
  const newValue = typeof value === 'number' ? [value] : []
  setSelectedNumbersForField(play.id, index, newValue)
}

const { setFormRef, validate } = useForm<FormValues>(
  Yup.object().shape({
    addOn: Yup.string().required(),
    numbers: Yup.array()
      .default([])
      .test(
        'allowed-combinations',
        getTranslation({
          fallback: t(`lottery.validation.field.numbers.notvalidforbettype`),
          key: `lottery.${lotteryId}.validation.field.numbers.notvalidforbettype`,
        }),
        (value) => validatePlay(play.perPlayAddOn?.value, value),
      ),
  }),
)

onMounted(() => {
  currentFields.value = play?.fields ?? []
  currentAddOn.value = play?.perPlayAddOn
  focusFirstEnabledInput()
})

watch(isPlayComplete, async (newValue) => {
  if (!newValue) return

  const isValid = await invokeValidate()
  if (isValid === play.isValid) return
  setValidPlay(play.id, isValid)
})

watch(
  [() => play.fields, () => play.perPlayAddOn],
  async () => {
    const isValid = await invokeValidate()
    if (!isPlayComplete.value || isValid === play.isValid) return
    setValidPlay(play.id, isValid)
  },
  { deep: true },
)

defineExpose<PickLotteryStepRef>({ validate: invokeValidate })
</script>
